import { gql } from "@apollo/client";
import {
  createListCollection,
  Editable,
  Grid,
  GridItem,
  Heading,
  Icon,
  IconButton,
  Portal,
  Select,
} from "@chakra-ui/react";
import { LuCircle, LuCircleCheck, LuPencil, LuTrash2 } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import type {
  TemplateSettingsForm_CategoryFragment,
  TemplateSettingsForm_TemplateFragment,
  Templates_Insert_Input,
  Templates_Set_Input,
} from "../graphql/generated/apollo";
import { Button } from "./ui/button";
import { Tag } from "./ui/tag";
import { Tooltip } from "./ui/tooltip";

gql`
  fragment TemplateSettingsForm_Template on templates {
    id
    name
    is_default
    category_templates {
      category {
        id
        name
      }
    }
  }

  fragment TemplateSettingsForm_Category on categories {
    id
    name
  }
`;

type TemplateSettingsFormProps = {
  templates: TemplateSettingsForm_TemplateFragment[];
  categories: TemplateSettingsForm_CategoryFragment[];
  selectedCategoryId: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  onAdd: (template: Templates_Insert_Input) => void;
  onUpdate: (template: Templates_Set_Input) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
};

function TemplateItem({
  template,
  onUpdate,
  onDelete,
  onSetDefault,
}: {
  template: TemplateSettingsForm_TemplateFragment;
  onUpdate: (template: TemplateSettingsForm_TemplateFragment) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <Grid
      templateColumns="1fr 1fr 36px 36px 36px"
      gap={2}
      alignItems="center"
      p={2}
      borderBottomWidth="1px"
      borderBottomColor="gray.200"
    >
      <GridItem>
        <Editable.Root
          defaultValue={template.name}
          onValueCommit={(details) => onUpdate({ ...template, name: details.value })}
        >
          <Editable.Preview />
          <Editable.Input textAlign="left" />
        </Editable.Root>
      </GridItem>
      <GridItem display="flex" gap={2} justifySelf="flex-end">
        {template.is_default && <Tag variant="outline">デフォルト</Tag>}
        {template.category_templates.map((ct) => (
          <Tag key={ct.category.id}>{ct.category.name}</Tag>
        ))}
      </GridItem>
      <GridItem justifySelf="center">
        {template.is_default ? (
          <Tooltip content="デフォルトに設定されています">
            <Icon asChild>
              <LuCircleCheck />
            </Icon>
          </Tooltip>
        ) : (
          <IconButton aria-label="デフォルトに設定" variant="ghost" size="sm" onClick={() => onSetDefault(template.id)}>
            <Tooltip content="デフォルトに設定">
              <LuCircle />
            </Tooltip>
          </IconButton>
        )}
      </GridItem>
      <GridItem justifySelf="center">
        <IconButton
          aria-label="編集"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/settings/templates/${template.id}/edit`)}
        >
          <LuPencil />
        </IconButton>
      </GridItem>
      <GridItem justifySelf="flex-end">
        <IconButton
          aria-label="削除"
          colorPalette="red"
          variant="ghost"
          size="sm"
          onClick={() => template.id && onDelete(template.id)}
        >
          <LuTrash2 />
        </IconButton>
      </GridItem>
    </Grid>
  );
}

export function TemplateSettingsForm({
  templates,
  categories,
  selectedCategoryId,
  onCategoryChange,
  onAdd,
  onUpdate,
  onDelete,
  onSetDefault,
}: TemplateSettingsFormProps) {
  // カテゴリーでフィルタリングされたテンプレートを取得
  const filteredTemplates = selectedCategoryId
    ? templates.filter((template) => template.category_templates.some((ct) => ct.category.id === selectedCategoryId))
    : templates;

  // 新しいテンプレートの追加
  const handleAddTemplate = () => {
    const newTemplate = {
      name: `テンプレート${templates.length + 1}`,
      is_default: templates.length === 0,
      category_templates: selectedCategoryId ? { data: [{ category_id: selectedCategoryId }] } : undefined,
    };

    onAdd(newTemplate);
  };

  const categoryCollection = createListCollection({
    items: categories,
    itemToString: (item) => item.name,
    itemToValue: (item) => item.id,
  });

  return (
    <>
      <Heading fontSize="lg" textAlign="left" py={2}>
        テンプレート設定
      </Heading>

      <Select.Root
        collection={categoryCollection}
        value={selectedCategoryId ? [selectedCategoryId] : []}
        onValueChange={(v) => onCategoryChange(v.value[0] ?? "")}
      >
        <Select.HiddenSelect />
        <Select.Label textAlign="left">カテゴリーで絞り込み</Select.Label>
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder="全てのカテゴリー" />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.ClearTrigger />
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {categories.map((category) => (
                <Select.Item item={category} key={category.id}>
                  {category.name}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>

      <Heading fontSize="sm" textAlign="left" py={2}>
        テンプレート管理
      </Heading>

      <Grid borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
        {filteredTemplates.length === 0 ? (
          <GridItem p={4} textAlign="center" color="gray.500">
            {selectedCategoryId ? "選択されたカテゴリーにはテンプレートがありません" : "テンプレートがありません"}
          </GridItem>
        ) : (
          filteredTemplates.map((template) => (
            <TemplateItem
              key={template.id}
              template={template}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onSetDefault={onSetDefault}
            />
          ))
        )}
      </Grid>

      <Button type="button" onClick={handleAddTemplate} mt={4}>
        テンプレートを追加
      </Button>
    </>
  );
}
